import api from 'src/utils/ajax';
import { populateRelationshipFromRequests } from 'src/utils/populateRelationship';

import { normalizeFbmResource } from '../../models/fbm-resource/normalizers';
import { createVisit, fetchFirstVisits } from '../../models/visit/visit';
import { normalize } from '../shared/normalizers';

import {
  Appointment,
  AppointmentStatus,
  EligibilityCheck,
  Service,
  WaitListEntry,
} from './_types';
import {
  denormalizeAppointment,
  denormalizeWaitListEntry,
  normalizeAppointment,
  normalizeCheckin,
  normalizeEligibilityCheck,
  normalizeService,
  normalizeWaitListEntry,
} from './normalizers';

import type { User } from '../../models/user/_types';
import type { Visit } from '../../models/visit/_types';
import type { Identifier, Timestamp } from '../../types/utilityTypes';

interface FetchAppointmentsOptions {
  page?: number; // Optional with a default value
  perPage?: number; // Optional with a default value
  onlyMe?: boolean; // some trainers can see ALL appointments
  view?: 'history' | 'upcoming' | 'canceled' | 'all';
  after?: Timestamp;
  before?: Timestamp;
  serviceId?: string;
  ids?: Identifier[];
}

interface FetchAppointmentsResponse {
  appointments: Appointment[];
  total: number;
}

export const fetchAppointments = async ({
  page = 1,
  perPage = 5,
  onlyMe = true,
  view = 'upcoming',
  after,
  before,
  serviceId,
  ids,
}: FetchAppointmentsOptions): Promise<FetchAppointmentsResponse> => {
  let params = {};
  if (serviceId) {
    params = { ...params, 'q[service_id_eq]': serviceId };
  }
  if (ids) {
    params = { ...params, with_ids: ids.join(',') };
  }
  const response = await api.get(`/api/v4/fbm/my_appointments`, {
    params: {
      page: page,
      per: perPage,
      view_all: onlyMe ? '' : 'true',
      view,
      after,
      before,
      ...params,
    },
  });

  const appointments = response.data.appointment.map((a) =>
    normalizeAppointment(a)
  );

  // Collect all unique user IDs from visits across all appointments
  const userIds = Array.from(
    new Set(
      appointments.flatMap((appointment) =>
        appointment.visits.map((visit) => visit.userId)
      )
    )
  ) as User['id'][];

  // Fetch first visits for all users
  const firstVisits = await fetchFirstVisits(userIds);

  // Add firstVisit flag to each visit in each appointment
  const appointmentsWithFirstVisitFlag = appointments.map((appointment) => ({
    ...appointment,
    visits: appointment.visits.map((visit) => {
      return {
        ...visit,
        firstVisit: firstVisits[visit.userId] === visit.id,
      };
    }),
  }));

  return {
    appointments: appointmentsWithFirstVisitFlag,
    total: response.data.meta.total,
  };
};

export const createAppointment = async (
  appointment: Partial<Appointment>
): Promise<Appointment> => {
  // set defaults
  appointment.status ||= AppointmentStatus.Scheduled;

  const response = await api.post(
    `/api/v4/fbm/appointments`,
    denormalizeAppointment(appointment)
  );
  return normalizeAppointment(response.data);
};

export const updateAppointment = async (
  appointment: Partial<Appointment>
): Promise<Appointment> => {
  const response = await api.put(
    `/api/v4/fbm/appointments/${appointment.id}`,
    denormalizeAppointment(appointment)
  );
  return normalizeAppointment(response.data);
};

export const deleteAppointment = async (
  appointment: Partial<Appointment>
): Promise<Appointment> => {
  const response = await api.delete(
    `/api/v4/fbm/appointments/${appointment.id}`
  );
  return normalizeAppointment(response.data);
};

export const modalAppointmentCheckIn = async (user: User): Promise<User> => {
  const response = await api.get(
    `/api/v4/fbm/check_in/frontdesk?code=${user.barcodeHash}`
  );

  return normalizeCheckin(response.data);
};

interface CheckAppointmentsOptions {
  appointment: Appointment;
  user: User;
}

export const checkAppointmentEligibility = async ({
  appointment,
  user,
}: CheckAppointmentsOptions): Promise<EligibilityCheck> => {
  const response = await api.get(
    `/api/v4/fbm/appointments/${appointment.id}/check_eligibility`,
    {
      params: {
        user_id: user.id,
      },
    }
  );

  return normalizeEligibilityCheck(response.data);
};

export const fetchServicesOptions = async (): Promise<Service[]> => {
  const response = await api.get(`/api/v4/fbm/services`, {
    params: {
      fetch_all: true,
      pluck_columns: 'id,name',
      'q[s]': 'name asc',
    },
  });

  return response.data.map(normalizeService);
};

interface FetchServicesOptions {
  includePrivate?: boolean;
  sort?: string;
}

export const fetchServices = async ({
  includePrivate = false,
  sort,
}: FetchServicesOptions): Promise<Service[]> => {
  const response = await api.get(`/api/v4/fbm/services`, {
    params: {
      fetch_all: true,
      ...(includePrivate && { include_private: true }),
      ...(sort && { q: { s: sort } }),
    },
  });

  return response.data.map((a) => normalizeService(a));
};

export const fetchServiceOptions = async (): Promise<Service[]> => {
  const response = await api.get(`/api/v4/fbm/services`, {
    params: {
      fetch_all: true,
      'q[s]': 'name asc',
    },
  });

  return response.data.map((a) => normalizeService(a));
};

export const fetchService = async (serviceId: Identifier): Promise<Service> => {
  const response = await api.get(`/api/v4/fbm/services/${serviceId}`);
  return normalizeService(response.data);
};

export const fetchResources = async (appointment: Appointment) => {
  const response = await api.get(`/api/v4/fbm/resources`, {
    params: { appointment_id: appointment.id, fetch_all: true },
  });

  return response.data.map((r) => normalizeFbmResource(r));
};

export const fetchAppointment = async (
  appointmentId: Identifier,
  withVisits = false
): Promise<Appointment> => {
  if (withVisits) {
    const response = await fetchAppointments({
      ids: [appointmentId],
      onlyMe: false,
      view: 'all',
    });

    return response.appointments[0];
  } else {
    const response = await api.get(`/api/v4/fbm/appointments/${appointmentId}`);
    return normalize(response.data);
  }
};

export const fetchWaitListEntries = async () => {
  const response = await api.get(`/api/v4/fbm/waitlist_entries`, {
    params: { for_user: true, fetch_all: true },
  });

  const list = response.data.map((w) => normalizeWaitListEntry(w));

  return await populateRelationshipFromRequests({
    list: list,
    idKey: 'appointmentId',
    newKey: 'appointment',
    func: async (id: Identifier) => {
      return await fetchAppointment(id);
    },
  });
};

interface CreateAppointmentWaitListEntryOptions {
  appointment: Appointment;
  waitListEntry: Partial<WaitListEntry>;
}

export const createAppointmentWaitListEntry = async ({
  appointment,
  waitListEntry,
}: CreateAppointmentWaitListEntryOptions) => {
  waitListEntry.appointmentId = appointment.id;

  const response = await api.post(
    `/api/v4/fbm/waitlist_entries/`,
    denormalizeWaitListEntry(waitListEntry)
  );

  return normalizeWaitListEntry(response.data);
};

export const deleteWaitListEntry = async (
  waitListEntry: Partial<WaitListEntry>
): Promise<WaitListEntry> => {
  const response = await api.delete(
    `/api/v4/fbm/waitlist_entries/${waitListEntry.id}`
  );

  return normalizeWaitListEntry(response.data);
};

interface CreateAppointmentVisitOptions {
  appointment: Appointment;
  visit: Partial<Visit>;
}
export const createAppointmentVisit = async ({
  appointment,
  visit,
}: CreateAppointmentVisitOptions) => {
  visit.appointmentId = appointment.id;
  return createVisit(visit);
};
